/**
 * KAN-752: поля "Основное" пользователя (оператора).
 *
 * Разметка вкладки "Основное" страницы оператора (/users/:id): шапка (почта,
 * вход, статус), имя и фамилия, шаблон, имя оператора для клиентов,
 * адаптация под стадию воронки. Состояние формы приходит из useUserEditForm.
 * Инструкции живут на своей вкладке (OperatorInstructionsFields).
 */
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import type { UserEditForm } from '@/hooks/useUserEditForm';
import type { User } from '@/types/user';

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Никогда';
  return new Date(dateString).toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/** Ошибка сохранения формы над полями. */
export function UserFormError({ message }: { message: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <div
      data-testid="form-error"
      className="bg-red-50 text-red-800 px-4 py-3 rounded-lg border border-red-200 text-sm mb-4">
      {message}
    </div>
  );
}

interface UserMainFieldsProps {
  user: User;
  form: UserEditForm;
  isCurrentUser?: boolean;
  /** Подсказка о вкладке со стадиями воронки (где настраиваются тексты стадий). */
  stagesHint?: string;
}

export function UserMainFields({
  user,
  form,
  isCurrentUser = false,
  stagesHint = 'Настройте тексты на вкладке "Стадии воронки".',
}: UserMainFieldsProps): JSX.Element {
  return (
    <>
      {isCurrentUser && (
        <div
          data-testid="self-edit-warning"
          className="bg-yellow-50 text-yellow-800 px-4 py-3 rounded-lg border border-yellow-200 text-sm mb-4">
          Вы редактируете свой аккаунт. Некоторые опции могут быть ограничены.
        </div>
      )}

      <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div
          data-testid="user-avatar"
          className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 font-medium">
          {getInitials(user.first_name, user.last_name)}
        </div>
        <div className="flex-1">
          <Input data-testid="email-display" value={user.email} readOnly className="bg-gray-100 mb-1" />
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span data-testid="last-login-info">Последний вход: {formatDate(user.last_login)}</span>
            <Badge data-testid="account-status" variant={user.is_active ? 'default' : 'outline'}>
              {user.is_active ? 'Активен' : 'Неактивен'}
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label htmlFor="first-name">Имя</Label>
          <Input
            id="first-name"
            data-testid="first-name-input"
            value={form.firstName}
            onChange={e => form.setFirstName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last-name">Фамилия</Label>
          <Input
            id="last-name"
            data-testid="last-name-input"
            value={form.lastName}
            onChange={e => form.setLastName(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-4 mt-2">
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <Label htmlFor="bot-mode-template">Шаблон</Label>
            <InfoTooltip text="Суфлёр - шаблон по умолчанию: бот подсказывает оператору информацию и расчёты в личном чате. Оператор и будущие шаблоны - отдельные истории с собственными каналами (Telegram-бот, телефония)." />
          </div>
          {/* Пока варианты грузятся, Radix сбрасывает значение в '': пустое
              игнорируем, чтобы сохранённый шаблон оставался выбранным. */}
          <Select
            value={form.template}
            onValueChange={v => {
              if (v) form.setTemplate(v);
            }}>
            <SelectTrigger id="bot-mode-template" data-testid="bot-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {form.roleTemplates.map(t => (
                <SelectItem key={t.codename} value={t.codename}>
                  {t.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Раздел оператора: для любого клиентского шаблона, у суфлёра скрыт. */}
        {!form.isSouffleur && (
          <div data-testid="operator-section" className="space-y-4 pt-2 border-t border-border">
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="operator-display-name">
                  Имя оператора (как представляться клиенту)
                  {/* TASK-349 AC-3: поле обязательно для клиентского шаблона. */}
                  <span
                    data-testid="operator-display-name-required"
                    aria-hidden="true"
                    className="ml-0.5 text-destructive">
                    *
                  </span>
                </Label>
                <InfoTooltip text="Клиент увидит это имя при знакомстве и в ответах бота" />
              </div>
              <Input
                id="operator-display-name"
                data-testid="operator-display-name-input"
                value={form.operatorDisplayName}
                onChange={e => form.setOperatorDisplayName(e.target.value)}
                placeholder="Например, Сергей"
                aria-invalid={form.isDisplayNameMissing}
                aria-required="true"
                className={
                  form.isDisplayNameMissing ? 'border-destructive focus-visible:ring-destructive' : undefined
                }
              />
              <p data-testid="operator-display-name-hint" className="text-xs text-muted-foreground">
                Имя, которым бот представляется клиентам. Без него клиентские каналы (голос, чат)
                включить нельзя.
              </p>
              {form.isDisplayNameMissing && (
                <p data-testid="operator-display-name-error" className="text-xs text-destructive">
                  Укажите имя: обязательно для шаблонов, общающихся с клиентом
                </p>
              )}
            </div>

            {/* Адаптация под стадию воронки: только у шаблонов с use_stage. */}
            {form.supportsStagePrompts && (
              <div className="space-y-2 pt-2 border-t border-border">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="use-stage-prompts">Адаптировать промпт под стадию воронки</Label>
                    <InfoTooltip text="Бот будет менять ЦЕЛЬ и ТАКТИКУ под текущую стадию клиента в воронке. Дерево услуг и расчёты остаются общими." />
                  </div>
                  <Switch
                    id="use-stage-prompts"
                    data-testid="use-stage-prompts-switch"
                    checked={form.useStagePrompts}
                    onCheckedChange={form.setUseStagePrompts}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Бот будет менять ЦЕЛЬ и ТАКТИКУ под текущую стадию клиента в воронке. Дерево услуг и
                  расчёты остаются общими.
                  {form.useStagePrompts && <> {stagesHint}</>}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default UserMainFields;
